import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { PublicShell } from "@/components/site-chrome";
import { Stamp } from "@/components/stamp";
import { BlogHero } from "@/components/blog-hero";
import { listBlogPosts } from "@/lib/blog.functions";
import { useState } from "react";
import { CLUSTERS } from "@/content/site-clusters.data";
import { clusterForPost } from "@/lib/site-clusters";

const postsQO = queryOptions({
  queryKey: ["blog", "list"],
  queryFn: () => listBlogPosts(),
});

export const Route = createFileRoute("/blog/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQO),
  head: () => ({
    meta: [
      { title: "AI Visibility & GEO Blog, Field Notes | monroya.ai" },
      {
        name: "description",
        content:
          "Field notes on AEO, GEO, and how AI assistants describe B2B SaaS categories. Tactics for getting cited by ChatGPT, Claude, Gemini, and Perplexity.",
      },
      { property: "og:title", content: "Monroya Blog" },
      {
        property: "og:description",
        content:
          "AEO, GEO, and how to get cited by AI assistants. No fluff.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.monroya.ai/blog" },
    ],
    links: [{ rel: "canonical", href: "https://www.monroya.ai/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Monroya Blog",
          url: "https://www.monroya.ai/blog",
          description:
            "AEO, GEO, and how AI assistants describe B2B SaaS categories.",
        }),
      },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const { data: posts } = useSuspenseQuery(postsQO);
  const [topic, setTopic] = useState("all");
  // Group posts by topic cluster; each group links to its pillar page.
  const groups = (() => {
    const map = new Map<string, { id: string; name: string; pillar: string | null; posts: typeof posts }>();
    for (const c of CLUSTERS) map.set(c.id, { id: c.id, name: c.name, pillar: c.pillar, posts: [] });
    const other = { id: "other", name: "More posts", pillar: null, posts: [] as typeof posts };
    for (const p of posts) {
      const c = clusterForPost({ slug: p.slug, tags: p.tags });
      (c ? map.get(c.id)! : other).posts.push(p);
    }
    return [...map.values(), other].filter((g) => g.posts.length > 0);
  })();

  return (
    <PublicShell>
      <section className="border-b border-border">
        <div className="site-container px-5 sm:px-6 py-20">
          <Stamp tone="amber">Field notes</Stamp>
          <h1 className="mt-5 font-display text-5xl font-semibold tracking-tight text-ink md:text-6xl">
            The Monroya blog
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            AEO, GEO, and the buyer-journey shift inside AI assistants. Tactics, teardowns, and named examples, written for operators.
          </p>
        </div>
      </section>

      <section className="site-container px-5 sm:px-6 py-16">
        {posts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-panel/40 p-12 text-center">
            <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              No posts yet
            </p>
            <p className="mt-3 text-ink">New posts ship soon. Check back shortly.</p>
          </div>
        ) : (
          <div>
            <div role="group" aria-label="Filter posts by topic" className="flex flex-wrap gap-2">
              {[{ id: "all", name: "All topics" }, ...groups.map((g) => ({ id: g.id, name: g.name }))].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={topic === f.id}
                  onClick={() => setTopic(f.id)}
                  className={`rounded-md border px-3 py-1.5 text-[13px] ${topic === f.id ? "border-ink bg-ink text-paper" : "border-border bg-paper text-ink hover:bg-panel"}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
            {groups.filter((g) => topic === "all" || topic === g.id).map((g) => (
              <section key={g.id} aria-labelledby={`topic-${g.id}`} className="mt-12">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 id={`topic-${g.id}`} className="font-display text-2xl font-semibold text-ink">{g.name}</h2>
                  {g.pillar && (
                    <Link to={g.pillar as never} className="text-[15px] text-ink underline underline-offset-2">
                      Read the {g.name} guide
                    </Link>
                  )}
                </div>
                <ul className="mt-6 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {g.posts.map((p) => (
                    <li key={p.slug}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group block overflow-hidden rounded-2xl border border-border bg-paper transition-all hover:-translate-y-1 hover:shadow-[0_12px_32px_-12px_rgba(26,31,54,0.18)]"
                >
                  <div className="flex aspect-[16/9] items-center justify-center overflow-hidden bg-panel">
                    {p.hero_image_url ? (
                      <img data-categories="essential"
                        src={p.hero_image_url}
                        alt={`Cover image for “${p.title}”`}
                        loading="lazy"
                        width={1200}
                        height={675}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <BlogHero
                        title={p.title}
                        imageUrl={null}
                        className="h-full w-full transition-transform duration-500 group-hover:scale-105 [&_img]:h-full [&_img]:object-cover"
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-1.5">
                      {(p.tags ?? []).filter((t) => !t.includes(":")).slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <h2 className="mt-2 font-display text-xl font-semibold leading-snug text-ink group-hover:text-[color:var(--brand-accent-ink)]">
                      {p.title}
                    </h2>
                    {/* Excerpt intentionally not shown on the public list. */}
                    {p.published_at && (
                      <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                        {new Date(p.published_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          timeZone: "UTC",
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </section>
    </PublicShell>
  );
}
